import React, { useMemo } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PaginationFooter } from '../../components/PaginationFooter';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useProjects } from '../../hooks/useProjects';
import type { ProjectListItem, ProjectScope } from '../../types/project';
import { formatDate } from '../../utils/dateTime';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'ProjectList'>;

function emptyProjectsMessage(scope: ProjectScope): string {
  if (scope === 'CREATOR') return 'Projects you create will appear here.';
  if (scope === 'MEMBER') return 'Projects assigned to you will appear here.';
  return 'Create a project when you have permission.';
}

function memberLabel(count: number): string {
  return count === 1 ? '1 member' : `${count} members`;
}

function ProjectCard({
  item,
  titleColor,
  metaColor,
  borderColor,
  surfaceColor,
  editColor,
  onPress,
  onEdit,
}: {
  item: ProjectListItem;
  titleColor: string;
  metaColor: string;
  borderColor: string;
  surfaceColor: string;
  editColor: string;
  onPress: () => void;
  /** Present only when the user may add members to existing projects. */
  onEdit?: () => void;
}) {
  return (
    <Pressable
      accessible
      accessibilityRole="button"
      accessibilityLabel={`Open ${item.projectName}`}
      onPress={onPress}
      style={[
        styles.card,
        { backgroundColor: surfaceColor, borderColor },
      ]}
    >
      <View style={styles.titleRow}>
        <Text
          accessible={false}
          style={[styles.title, styles.titleText, { color: titleColor }]}
        >
          {item.projectName}
        </Text>
        {onEdit ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Edit ${item.projectName}`}
            onPress={onEdit}
            hitSlop={8}
            style={[styles.editButton, { borderColor: editColor }]}
          >
            <Text style={[styles.editText, { color: editColor }]}>Edit</Text>
          </Pressable>
        ) : null}
      </View>
      <Text accessible={false} style={[styles.meta, { color: metaColor }]}>
        {memberLabel(item.memberCount)}
      </Text>
      <Text accessible={false} style={[styles.meta, { color: metaColor }]}>
        Status: {item.statusLabel}
      </Text>
      <Text accessible={false} style={[styles.meta, { color: metaColor }]}>
        Created: {formatDate(item.createdAt)}
      </Text>
      {item.description ? (
        <Text
          accessible={false}
          style={[styles.meta, { color: metaColor }]}
          numberOfLines={2}
        >
          {item.description}
        </Text>
      ) : null}
    </Pressable>
  );
}

export function ProjectListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
      loading: { ...typography.body, color: palette.textSecondary },
      add: {
        color: palette.white,
        fontSize: 28,
        lineHeight: 30,
        fontWeight: '300' as const,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getProjectModuleCapabilities(auth.permissions, auth.roles);
  const list = useProjects();
  // Focus refresh is handled inside usePaginatedMasterData (via useProjects).
  const hasFilters = Boolean(list.search.trim()) || list.filters.isActive != null;

  const footer = useMemo(
    () => <PaginationFooter loading={list.isLoadingMore} />,
    [list.isLoadingMore],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.PROJECT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Projects"
          onBack={navigation.goBack}
          right={
            capabilities.canCreateProject ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Create Project"
                onPress={() => navigation.navigate(routes.projectCreate)}
                hitSlop={8}
                style={styles.addHit}
              >
                <Text style={themed.add}>+</Text>
              </Pressable>
            ) : null
          }
        />
        <View style={styles.body}>
          <AppInput
            label="Search"
            value={list.search}
            onChangeText={list.setSearch}
            placeholder="Project name"
            autoCapitalize="none"
          />
          <View style={styles.filterRow}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Show active projects"
              onPress={() => list.applyFilters({ isActive: true })}
              style={themed.chip}
            >
              <Text style={themed.chipText}>Active</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Show inactive projects"
              onPress={() => list.applyFilters({ isActive: false })}
              style={themed.chip}
            >
              <Text style={themed.chipText}>Inactive</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear status filter"
              onPress={() => list.applyFilters({})}
              style={themed.chip}
            >
              <Text style={themed.chipText}>All</Text>
            </Pressable>
          </View>

          {list.isLoading && list.items.length === 0 ? (
            <View style={styles.center}>
              <Text style={themed.loading}>Loading projects…</Text>
            </View>
          ) : list.error && list.items.length === 0 ? (
            <ErrorState message={list.error} onRetry={list.retry} />
          ) : (
            <FlatList
              data={list.items}
              keyExtractor={item => String(item.projectId)}
              refreshControl={
                <RefreshControl
                  refreshing={list.isRefreshing}
                  onRefresh={list.refresh}
                />
              }
              onEndReached={list.loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title={
                    hasFilters
                      ? 'No records match the current search.'
                      : 'No projects were found.'
                  }
                  message={
                    hasFilters
                      ? 'Try clearing search or filters.'
                      : emptyProjectsMessage(capabilities.scope)
                  }
                  actionTitle={hasFilters ? 'Reset filters' : 'Retry'}
                  onAction={hasFilters ? list.resetFilters : list.retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <ProjectCard
                  item={item}
                  titleColor={palette.textPrimary}
                  metaColor={palette.textSecondary}
                  borderColor={palette.border}
                  surfaceColor={palette.surface}
                  editColor={palette.primary}
                  onEdit={
                    capabilities.canEditProjectMembers
                      ? () =>
                          navigation.navigate(routes.projectEdit, {
                            projectId: item.projectId,
                            projectName: item.projectName,
                          })
                      : undefined
                  }
                  onPress={() =>
                    navigation.navigate(routes.projectTaskBoard, {
                      projectId: item.projectId,
                      projectName: item.projectName,
                    })
                  }
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  addHit: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    borderWidth: 1,
    borderRadius: 12,
    padding: spacing.lg,
    gap: spacing.xs,
  },
  title: { ...typography.body, fontWeight: '600' },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  titleText: { flex: 1 },
  editButton: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  editText: { ...typography.caption, fontWeight: '700' },
  meta: { ...typography.caption },
});
